import { useEffect, useState, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError, uploadFile } from "@/lib/api";
import { computeFinancials } from "@/lib/contractCalc";
import { fmtDate } from "@/lib/utils";
import { PageHeader, KpiTile, currency } from "@/components/Primitives";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Plus, Download, Upload, Search, Pencil, Trash2, Receipt, BarChart3, TrendingUp, FileText, DollarSign } from "lucide-react";
import { AgentContact } from "@/components/AgentContact";

const STATUS_TONE = {
  "active listing": "bg-[#E7F0EA] text-[#2E5C47] border-[#bcd4c4]",
  "under contract": "bg-[#FBEAE1] text-[#F7941D] border-[#eccab8]",
  closed: "bg-[#1F3C88] text-white border-[#1F3C88]",
  cancelled: "bg-[#F6E4E4] text-[#992B2B] border-[#e0bcbc]",
};
const STATUS_OPTIONS = ["active listing", "under contract", "closed", "cancelled"];
const TITLECASE = (s) => (s || "").replace(/\b\w/g, (c) => c.toUpperCase());

const INITIAL = {
  agent_name: "", transaction_type: "purchase", status: "active listing", close_date: "", creation_date: "",
  address: "", city: "", state: "", zip_code: "", first_name: "", last_name: "",
  lead_type: "", lead_generated_by: "", lender: "", title_company: "",
  contract_value: "", commission_rate: "", broker_split: "no", broker_split_percentage: "20",
  risk_management_fee: "0", cap_fee_100: "0", transaction_review_fee: "0",
  transaction_coordinator_fee: "499.00", referral_fee: "0", coordinator_name: "", tm_transaction_id: "",
};

const inp = "w-full px-3 py-2 bg-white border border-[#E5E5E2] rounded-sm text-sm focus:ring-2 focus:ring-[#F7941D] focus:border-transparent focus:outline-none transition-colors duration-200";
const lbl = "text-[11px] tracking-[0.12em] uppercase font-semibold text-[#575755] block mb-1";

export default function SalesTracker() {
  const { user } = useAuth();
  const canWrite = user.access?.sales ? user.access.sales === "write" : (user.role === "admin" || user.role === "sales_manager");
  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: 6 }, (_, i) => currentYear - i);

  const [year, setYear] = useState(currentYear);
  const [contracts, setContracts] = useState([]);
  const [stats, setStats] = useState(null);
  const [rosters, setRosters] = useState({ agents: [], lenders: [], title_companies: [], lead_types: [] });
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("under contract");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(INITIAL);
  const fileRef = useRef(null);
  const [importing, setImporting] = useState(false);
  const [auditId, setAuditId] = useState(null);
  const [coordNames, setCoordNames] = useState([]);
  const [tmFiles, setTmFiles] = useState([]);
  const [fileQuery, setFileQuery] = useState("");
  useEffect(() => { api.get("/contracts/coordinators").then((r) => setCoordNames(r.data)).catch(() => {}); }, []);
  useEffect(() => { if (open) { api.get("/contracts/tm-files").then((r) => setTmFiles(r.data)).catch(() => {}); setFileQuery(""); } }, [open]);

  const load = useCallback(async () => {
    const [c, s] = await Promise.all([
      api.get("/contracts", { params: { year } }),
      api.get("/analytics/summary", { params: { year } }),
    ]);
    setContracts(c.data);
    setStats(s.data);
  }, [year]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    Promise.all([
      api.get("/agents"), api.get("/lenders"), api.get("/title-companies"), api.get("/lead-types"), api.get("/people", { params: { type: "agent", roster: 1 } }),
    ]).then(([a, l, t, ld, ppl]) => {
      const agentNames = Array.from(new Set([...a.data.map((x) => x.name), ...ppl.data.map((x) => x.name)])).sort();
      setRosters({
        agents: agentNames, lenders: l.data.map((x) => x.name),
        title_companies: t.data.map((x) => x.name), lead_types: ld.data.map((x) => x.name),
      });
    }).catch(() => {});
  }, []);

  const openNew = () => { setEditing(null); setForm(INITIAL); setOpen(true); };
  const openEdit = (c) => {
    setEditing(c);
    setForm({ ...INITIAL, ...Object.fromEntries(Object.keys(INITIAL).map((k) => [k, c[k] ?? INITIAL[k]])) });
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    const payload = { ...form, contract_value: parseFloat(form.contract_value) || 0, commission_rate: parseFloat(form.commission_rate) || 0 };
    try {
      if (editing) { await api.put(`/contracts/${editing.id}`, payload); toast.success("Contract updated"); }
      else { await api.post("/contracts", payload); toast.success("Contract created"); }
      setOpen(false);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const changeStatus = async (c, status) => {
    try { await api.put(`/contracts/${c.id}`, { status }); toast.success("Status updated"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const remove = async (c) => {
    try { await api.delete(`/contracts/${c.id}`); toast.success("Contract deleted"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const CSV_COLS = [
    "agent_name", "transaction_type", "status", "close_date", "creation_date", "address", "city", "state", "zip_code",
    "first_name", "last_name", "lead_type", "lead_generated_by", "lender", "title_company",
    "contract_value", "commission_rate", "broker_split", "broker_split_percentage",
    "risk_management_fee", "cap_fee_100", "transaction_review_fee", "transaction_coordinator_fee", "referral_fee",
    "gross_commission", "lead_cost", "agent_commission", "company_net",
  ];

  const csvCell = (x) => `"${String(x ?? "").replace(/"/g, '""')}"`;

  const exportCSV = () => {
    const rows = [CSV_COLS];
    filtered.forEach((c) => {
      const f = c.financials || computeFinancials(c);
      rows.push(CSV_COLS.map((k) => {
        if (k === "gross_commission") return Math.round((f.gross ?? f.gross_commission) || 0);
        if (k === "lead_cost") return Math.round((f.lead ?? f.lead_cost) || 0);
        if (k === "agent_commission") return Math.round((f.agent ?? f.agent_commission) || 0);
        if (k === "company_net") return Math.round((f.companyNet ?? f.company_net) || 0);
        return c[k] ?? "";
      }));
    });
    const csv = rows.map((r) => r.map(csvCell).join(",")).join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a"); a.href = url; a.download = `contracts-${year}.csv`; a.click();
    URL.revokeObjectURL(url);
    toast.success("Exported to CSV");
  };

  const onImportFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setImporting(true);
    try {
      const res = await uploadFile(file, "/contracts/import");
      if (res.imported > 0) {
        setStatusFilter("all");
        const extra = [res.duplicates ? `${res.duplicates} duplicate${res.duplicates === 1 ? "" : "s"} skipped` : "", res.skipped ? `${res.skipped} invalid skipped` : ""].filter(Boolean).join(" · ");
        toast.success(`Imported ${res.imported} contract${res.imported === 1 ? "" : "s"}${extra ? ` · ${extra}` : ""}`);
      } else {
        const extra = [res.duplicates ? `${res.duplicates} duplicates` : "", res.skipped ? `${res.skipped} invalid` : ""].filter(Boolean).join(" · ");
        toast[res.duplicates ? "info" : "error"](res.duplicates ? `No new rows — ${extra} (all already imported)` : (res.skipped ? `No rows imported · ${res.skipped} skipped. Check the file columns.` : "No rows found in that file."));
      }
      if (res.errors?.length) console.warn("Import row errors:", res.errors);
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setImporting(false);
    }
  };

  const [sortKey, setSortKey] = useState("close_date");
  const [sortDir, setSortDir] = useState("asc");
  const toggleSort = (k) => { if (sortKey === k) setSortDir((d) => (d === "asc" ? "desc" : "asc")); else { setSortKey(k); setSortDir("asc"); } };

  const nowYM = (() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`; })();
  const filtered = contracts.filter((c) => {
    const t = search.toLowerCase();
    const match = [c.first_name, c.last_name, c.address, c.city, c.agent_name, c.coordinator?.name].some((v) => (v || "").toLowerCase().includes(t));
    if (statusFilter === "__accepted_this_month__") return match && (c.creation_date || "").startsWith(nowYM);
    return match && (statusFilter === "all" || c.status === statusFilter);
  }).sort((a, b) => {
    const da = a[sortKey] || "", db = b[sortKey] || "";
    if (!da && !db) return 0;
    if (!da) return 1;
    if (!db) return -1;
    return sortDir === "asc" ? da.localeCompare(db) : db.localeCompare(da);
  });

  const totValue = filtered.reduce((s, c) => s + (parseFloat(c.contract_value) || 0), 0);
  const totComm = filtered.reduce((s, c) => s + ((c.financials?.gross_commission) || 0), 0);
  const totNet = filtered.reduce((s, c) => s + ((c.financials?.company_net) || 0), 0);

  const rosterSelect = (name, list) => (
    <>
      <input className={inp} list={`dl-${name}`} value={form[name]} placeholder="Select or type to add new…"
        onChange={(e) => setForm({ ...form, [name]: e.target.value })} />
      <datalist id={`dl-${name}`}>
        {list.map((x) => <option key={x} value={x} />)}
      </datalist>
    </>
  );

  const live = computeFinancials(form);

  return (
    <div data-testid="sales-page">
      <PageHeader
        title="Sales Tracker"
        subtitle="Your Company Deal Tracker — every contract, commission and company-net figure, with pipeline and closed-business KPIs."
        action={
          <div className="flex items-center gap-3">
            <select data-testid="year-filter" value={year} onChange={(e) => setYear(parseInt(e.target.value))}
              className="px-4 py-2.5 bg-white border border-[#E5E5E2] rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-[#F7941D]">
              {yearOptions.map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
            <Link to="/sales/analytics" data-testid="analytics-link" className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full border border-[#1F3C88] text-[#1F3C88] text-sm font-semibold hover:bg-[#1F3C88] hover:text-white transition-colors duration-200">
              <BarChart3 className="w-4 h-4" /> Analytics
            </Link>
            {canWrite && (
              <button data-testid="export-csv-button" onClick={exportCSV} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full border border-[#1F3C88] text-[#1F3C88] text-sm font-semibold hover:bg-[#1F3C88] hover:text-white transition-colors duration-200">
                <Download className="w-4 h-4" /> CSV
              </button>
            )}
            {canWrite && (
              <>
                <input ref={fileRef} type="file" accept=".csv,text/csv" onChange={onImportFile} className="hidden" data-testid="import-csv-input" />
                <button data-testid="import-csv-button" disabled={importing} onClick={() => fileRef.current?.click()}
                  className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full border border-[#1F3C88] text-[#1F3C88] text-sm font-semibold hover:bg-[#1F3C88] hover:text-white transition-colors duration-200 disabled:opacity-60">
                  <Upload className="w-4 h-4" /> {importing ? "Importing…" : "Import"}
                </button>
              </>
            )}
            {canWrite && (
              <button data-testid="add-deal-button" onClick={openNew} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#F7941D] text-white text-sm font-semibold hover:bg-[#E08312] transition-colors duration-200">
                <Plus className="w-4 h-4" /> Add Contract
              </button>
            )}
          </div>
        }
      />

      {stats && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
          <KpiTile index={0} testid="kpi-volume-ytd" color="#16A34A" Icon={TrendingUp} value={stats.volume_count} label="Volume YTD"
            rows={[["Closed & Pending:", currency(stats.volume_total)], ["Total GCI:", currency(stats.volume_gci)]]} />
          <KpiTile index={1} testid="kpi-active-listings" color="#2563EB" Icon={TrendingUp} value={stats.active_count} label="Active Listings"
            rows={[["Volume:", currency(stats.active_volume)], ["Gross GCI:", currency(stats.active_gci)]]} />
          <KpiTile index={2} testid="kpi-pending-contracts" color="#F59E0B" Icon={FileText} value={stats.pending_count} label="Pending Contracts"
            rows={[["Volume:", currency(stats.pending_volume)], ["Gross GCI:", currency(stats.pending_gci)]]} />
          <KpiTile index={3} testid="kpi-closed-transactions" color="#16A34A" Icon={DollarSign} value={stats.closed_count} label={`Closed ${year}`}
            rows={[["Volume:", currency(stats.closed_volume)], ["Gross GCI:", currency(stats.closed_gci)]]} />
        </div>
      )}

      <div className="bg-white border border-[#E5E5E2] rounded-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-[#E5E5E2] flex flex-col sm:flex-row gap-3 sm:items-center justify-between">
          <div className="relative w-full sm:w-72">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#8a8a86]" />
            <input data-testid="contract-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search client, agent, address…"
              className="w-full pl-9 pr-3 py-2 bg-white border border-[#E5E5E2] rounded-sm text-sm focus:ring-2 focus:ring-[#F7941D] focus:outline-none" />
          </div>
          <select data-testid="status-filter" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 bg-white border border-[#E5E5E2] rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-[#F7941D]">
            <option value="all">All statuses</option>
            <option value="__accepted_this_month__">Accepted this Month</option>
            {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{TITLECASE(s)}</option>)}
          </select>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[#E5E5E2] text-left text-xs tracking-[0.12em] uppercase text-[#575755]">
                <th className="px-4 py-3 font-semibold">Agent</th>
                <th className="px-4 py-3 font-semibold">Coordinator</th>
                <th className="px-4 py-3 font-semibold">Property</th>
                <th className="px-4 py-3 font-semibold">Client</th>
                <th className="px-4 py-3 font-semibold text-right">Value</th>
                <th className="px-4 py-3 font-semibold text-right">Comm</th>
                <th className="px-4 py-3 font-semibold text-right">Company Net</th>
                <th className="px-4 py-3 font-semibold">
                  <button type="button" data-testid="sort-accepted" onClick={() => toggleSort("creation_date")} className="inline-flex items-center gap-1 hover:text-[#1F3C88] transition-colors duration-200">
                    Accepted <span className={sortKey === "creation_date" ? "text-[#F7941D]" : "text-[#C7C4BD]"}>{sortKey === "creation_date" ? (sortDir === "asc" ? "↑" : "↓") : "↕"}</span>
                  </button>
                </th>
                <th className="px-4 py-3 font-semibold">
                  <button type="button" data-testid="sort-close" onClick={() => toggleSort("close_date")} className="inline-flex items-center gap-1 hover:text-[#1F3C88] transition-colors duration-200">
                    Close <span className={sortKey === "close_date" ? "text-[#F7941D]" : "text-[#C7C4BD]"}>{sortKey === "close_date" ? (sortDir === "asc" ? "↑" : "↓") : "↕"}</span>
                  </button>
                </th>
                <th className="px-4 py-3 font-semibold">Status</th>
                {canWrite && <th className="px-4 py-3 font-semibold text-right">Actions</th>}
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan={canWrite ? 11 : 10} className="px-4 py-10 text-center text-[#8a8a86]" data-testid="no-contracts">No contracts for this filter.</td></tr>
              ) : filtered.map((c) => {
                const f = c.financials || {};
                return (
                  <tr key={c.id} data-testid={`contract-row-${c.id}`} className="border-b border-[#F0EFEC] hover:bg-[#FBFAF8] transition-colors duration-200">
                    <td className="px-4 py-3"><AgentContact profile={c.agent_profile} name={c.agent_name} /><div className="text-xs text-[#a3a099] capitalize mt-1">{c.transaction_type}</div></td>
                    <td className="px-4 py-3 text-[#575755] whitespace-nowrap" data-testid={`contract-coordinator-${c.id}`}>
                      {c.coordinator?.name ? (
                        <><div className="text-[#0A0A0A]">{c.coordinator.name}</div>
                          {c.coordinator.source === "tm" || c.coordinator.source === "linked"
                            ? <a href={`/transactions/${c.coordinator.transaction_id}`} className="text-[11px] text-[#1F3C88] hover:underline">{c.coordinator.source === "linked" ? "Linked TM file" : "From TM file"}</a>
                            : <div className="text-[11px] text-[#a3a099]">Set on contract</div>}</>
                      ) : c.coordinator?.source === "linked"
                        ? <a href={`/transactions/${c.coordinator.transaction_id}`} className="text-[11px] text-[#1F3C88] hover:underline">Linked file · no coordinator yet</a>
                        : <span className="text-[#C7C4BD]">—</span>}
                    </td>
                    <td className="px-4 py-3"><button type="button" data-testid={`open-contract-${c.id}`} onClick={() => openEdit(c)} className="text-left font-medium text-[#1F3C88] hover:text-[#F7941D] hover:underline transition-colors duration-200">{c.address || "—"}</button><div className="text-xs text-[#575755]">{c.city}, {c.state}</div></td>
                    <td className="px-4 py-3 text-[#575755]">{c.first_name} {c.last_name}<div className="text-xs text-[#a3a099]">{c.lead_type}</div></td>
                    <td className="px-4 py-3 text-right tabular-nums">{currency(c.contract_value)}</td>
                    <td className="px-4 py-3 text-right tabular-nums">{currency(f.gross_commission || 0)}<div className="text-xs text-[#a3a099]">{c.commission_rate}%</div></td>
                    <td className="px-4 py-3 text-right">
                      <CommissionBreakdown contract={c} openId={auditId} setOpenId={setAuditId} />
                    </td>
                    <td className="px-4 py-3 text-[#575755] whitespace-nowrap">{fmtDate(c.creation_date)}</td>
                    <td className="px-4 py-3 text-[#575755] whitespace-nowrap">{fmtDate(c.close_date)}</td>
                    <td className="px-4 py-3">
                      <select disabled={!canWrite} value={c.status} onChange={(e) => changeStatus(c, e.target.value)}
                        data-testid={`status-select-${c.id}`}
                        className={`text-xs font-medium px-2.5 py-1 rounded-full border capitalize focus:outline-none ${STATUS_TONE[c.status] || STATUS_TONE["active listing"]} ${canWrite ? "cursor-pointer" : ""}`}>
                        {STATUS_OPTIONS.map((s) => <option key={s} value={s} className="bg-white text-[#0A0A0A]">{TITLECASE(s)}</option>)}
                      </select>
                    </td>
                    {canWrite && (
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1">
                          <button data-testid={`edit-contract-${c.id}`} onClick={() => openEdit(c)} className="p-2 rounded-sm text-[#575755] hover:bg-[#F0EFEC] hover:text-[#1F3C88] transition-colors duration-200"><Pencil className="w-4 h-4" /></button>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <button data-testid={`delete-contract-${c.id}`} className="p-2 rounded-sm text-[#575755] hover:bg-[#F6E4E4] hover:text-[#992B2B] transition-colors duration-200"><Trash2 className="w-4 h-4" /></button>
                            </AlertDialogTrigger>
                            <AlertDialogContent className="bg-white">
                              <AlertDialogHeader>
                                <AlertDialogTitle className="font-serif text-2xl">Delete this contract?</AlertDialogTitle>
                                <AlertDialogDescription>{c.address} — {c.first_name} {c.last_name} · {currency(c.contract_value)}. This cannot be undone.</AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction data-testid={`confirm-delete-${c.id}`} onClick={() => remove(c)} className="bg-[#992B2B] hover:bg-[#7d2222]">Delete</AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
            {filtered.length > 0 && (
              <tfoot>
                <tr data-testid="sales-totals-row" className="border-t-2 border-[#E5E5E2] bg-[#FBFAF8] font-semibold text-[#0A0A0A]">
                  <td className="px-4 py-3" colSpan={3}>Totals · {filtered.length} contract{filtered.length === 1 ? "" : "s"}</td>
                  <td className="px-4 py-3 text-right tabular-nums" data-testid="totals-value">{currency(totValue)}</td>
                  <td className="px-4 py-3 text-right tabular-nums" data-testid="totals-comm">{currency(totComm)}</td>
                  <td className="px-4 py-3 text-right tabular-nums" data-testid="totals-net">{currency(totNet)}</td>
                  <td className="px-4 py-3" colSpan={canWrite ? 4 : 3}></td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>

      {/* Add / Edit Contract dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">{editing ? "Edit Contract" : "New Contract"}</DialogTitle>
            <DialogDescription className="text-sm text-[#575755]">Enter the agent, property, client and financial details. Commission and company-net update live below.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-5" data-testid="contract-form">
            <Section title="Agent & Transaction">
              <div className="grid grid-cols-2 gap-3">
                <div><span className={lbl}>Agent</span>{rosterSelect("agent_name", rosters.agents)}</div>
                <div><span className={lbl}>Transaction Type</span>
                  <select className={inp} value={form.transaction_type} onChange={(e) => setForm({ ...form, transaction_type: e.target.value })}>
                    {["purchase", "listing", "rental"].map((t) => <option key={t} value={t}>{TITLECASE(t)}</option>)}
                  </select>
                </div>
                <div><span className={lbl}>Status</span>
                  <select className={inp} value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                    {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{TITLECASE(s)}</option>)}
                  </select>
                </div>
                <div><span className={lbl}>Accepted Date</span><input type="date" data-testid="creation-date-input" className={inp} value={form.creation_date} onChange={(e) => setForm({ ...form, creation_date: e.target.value })} /></div>
                <div><span className={lbl}>Close Date</span><input type="date" className={inp} value={form.close_date} onChange={(e) => setForm({ ...form, close_date: e.target.value })} /></div>
              </div>
            </Section>

            <Section title="Property">
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2"><span className={lbl}>Address</span><input required data-testid="contract-address-input" className={inp} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></div>
                <div><span className={lbl}>City</span><input className={inp} value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><span className={lbl}>State</span><input className={inp} value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} /></div>
                  <div><span className={lbl}>Zip</span><input className={inp} value={form.zip_code} onChange={(e) => setForm({ ...form, zip_code: e.target.value })} /></div>
                </div>
              </div>
            </Section>

            <Section title="Client & Lead">
              <div className="grid grid-cols-2 gap-3">
                <div><span className={lbl}>First Name</span><input className={inp} value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} /></div>
                <div><span className={lbl}>Last Name</span><input className={inp} value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} /></div>
                <div><span className={lbl}>Lead Type</span>{rosterSelect("lead_type", rosters.lead_types)}</div>
                <div className="col-span-2"><span className={lbl}>Transaction Manager file</span>
                  <input data-testid="tm-file-search" className={`${inp} mb-1.5`} placeholder="Search files by address or coordinator…" value={fileQuery} onChange={(e) => setFileQuery(e.target.value)} />
                  <select data-testid="tm-file-select" className={inp} value={form.tm_transaction_id || ""} onChange={(e) => setForm({ ...form, tm_transaction_id: e.target.value })}>
                    <option value="">Auto: match by address{editing?.coordinator?.source === "tm" ? ` (matched ${tmFiles.find((t) => t.transaction_id === editing.coordinator.transaction_id)?.property_address || "a file"})` : ""}</option>
                    {tmFiles.filter((t) => t.transaction_id === form.tm_transaction_id || `${t.property_address} ${t.coordinator_name || ""}`.toLowerCase().includes(fileQuery.toLowerCase())).map((t) => (
                      <option key={t.transaction_id} value={t.transaction_id}>{t.property_address}{t.coordinator_name ? ` · ${t.coordinator_name}` : " · no coordinator"}{t.status ? ` · ${t.status.replace("_", " ")}` : ""}</option>
                    ))}
                  </select>
                  <p className="text-[11px] text-[#8a8a86] mt-1">Link the exact file when the addresses differ. If the TM file doesn't exist yet, pick the coordinator below and link the file later.</p>
                </div>
                <div className="col-span-2"><span className={lbl}>Coordinator</span>
                  <select data-testid="coordinator-select" className={inp} value={form.coordinator_name || ""} onChange={(e) => setForm({ ...form, coordinator_name: e.target.value })}>
                    <option value="">{form.tm_transaction_id ? "From the linked Transaction Manager file" : `Auto: from the matching Transaction Manager file${editing?.coordinator?.source === "tm" ? ` (${editing.coordinator.name})` : ""}`}</option>
                    {Array.from(new Set([...coordNames, form.coordinator_name].filter(Boolean))).map((n) => <option key={n} value={n}>{n}</option>)}
                  </select>
                </div>
                <div><span className={lbl}>Lead Generated By</span>
                  <select className={inp} value={form.lead_generated_by} onChange={(e) => setForm({ ...form, lead_generated_by: e.target.value })}>
                    <option value="">Select…</option>
                    <option>Company Generated</option>
                    <option>Agent Generated</option>
                  </select>
                </div>
                <div><span className={lbl}>Lender</span>{rosterSelect("lender", rosters.lenders)}</div>
                <div><span className={lbl}>Title Company</span>{rosterSelect("title_company", rosters.title_companies)}</div>
              </div>
            </Section>

            <Section title="Financials">
              <div className="grid grid-cols-2 gap-3">
                <div><span className={lbl}>Contract Value</span><input required type="number" data-testid="contract-value-input" className={inp} value={form.contract_value} onChange={(e) => setForm({ ...form, contract_value: e.target.value })} /></div>
                <div><span className={lbl}>Commission Rate %</span><input required type="number" step="0.01" data-testid="commission-rate-input" className={inp} value={form.commission_rate} onChange={(e) => setForm({ ...form, commission_rate: e.target.value })} /></div>
                <div><span className={lbl}>Broker Split</span>
                  <select className={inp} value={form.broker_split} onChange={(e) => setForm({ ...form, broker_split: e.target.value })}>
                    <option value="no">No</option><option value="yes">Yes</option>
                  </select>
                </div>
                {form.broker_split === "yes" && <div><span className={lbl}>Broker Split %</span><input type="number" className={inp} value={form.broker_split_percentage} onChange={(e) => setForm({ ...form, broker_split_percentage: e.target.value })} /></div>}
                <div><span className={lbl}>Risk Mgmt Fee</span><input type="number" className={inp} value={form.risk_management_fee} onChange={(e) => setForm({ ...form, risk_management_fee: e.target.value })} /></div>
                <div><span className={lbl}>Cap Fee (100%)</span><input type="number" className={inp} value={form.cap_fee_100} onChange={(e) => setForm({ ...form, cap_fee_100: e.target.value })} /></div>
                <div><span className={lbl}>Transaction Review Fee</span><input type="number" className={inp} value={form.transaction_review_fee} onChange={(e) => setForm({ ...form, transaction_review_fee: e.target.value })} /></div>
                <div><span className={lbl}>TC Fee</span><input type="number" className={inp} value={form.transaction_coordinator_fee} onChange={(e) => setForm({ ...form, transaction_coordinator_fee: e.target.value })} /></div>
                <div><span className={lbl}>Referral Fee</span><input type="number" className={inp} value={form.referral_fee} onChange={(e) => setForm({ ...form, referral_fee: e.target.value })} /></div>
              </div>
              <div className="mt-4 grid grid-cols-3 gap-3 bg-[#F3F6F4] rounded-sm p-4">
                <div><div className="text-[10px] tracking-[0.12em] uppercase text-[#575755]">Gross Commission</div><div className="font-serif text-xl text-[#1F3C88]">{currency(live.gross)}</div></div>
                <div><div className="text-[10px] tracking-[0.12em] uppercase text-[#575755]">Agent Commission</div><div className="font-serif text-xl text-[#1F3C88]">{currency(live.agent)}</div></div>
                <div><div className="text-[10px] tracking-[0.12em] uppercase text-[#575755]">Company Net</div><div data-testid="company-net-display" className="font-serif text-xl text-[#F7941D]">{currency(live.companyNet)}</div></div>
              </div>
            </Section>

            <button data-testid="contract-submit-button" className="w-full py-3 rounded-full bg-[#1F3C88] text-white font-semibold hover:bg-[#152A61] transition-colors duration-200">
              {editing ? "Save Changes" : "Create Contract"}
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <div className="text-xs tracking-[0.15em] uppercase font-semibold text-[#F7941D] mb-3">{title}</div>
      {children}
    </div>
  );
}

function CommissionBreakdown({ contract, openId, setOpenId }) {
  const f = contract.financials || computeFinancials(contract);
  const gross = f.gross ?? f.gross_commission ?? 0;
  const net = f.companyNet ?? f.company_net ?? 0;
  const deductions = [
    { label: "Lead Cost", value: f.lead ?? f.lead_cost ?? 0 },
    { label: "Agent Commission", value: f.agent ?? f.agent_commission ?? 0 },
    { label: "Broker Split", value: f.broker ?? f.broker_split ?? 0 },
    { label: "Risk Mgmt Fee", value: Number(contract.risk_management_fee) || 0 },
    { label: "Cap Fee (100%)", value: Number(contract.cap_fee_100) || 0 },
    { label: "Transaction Review Fee", value: Number(contract.transaction_review_fee) || 0 },
    { label: "TC Fee", value: Number(contract.transaction_coordinator_fee) || 0 },
    { label: "Referral Fee", value: Number(contract.referral_fee) || 0 },
  ];
  const row = "flex items-center justify-between py-1.5 text-sm";
  return (
    <Popover open={openId === contract.id} onOpenChange={(o) => setOpenId(o ? contract.id : null)}>
      <PopoverTrigger asChild>
        <button
          data-testid={`commission-audit-${contract.id}`}
          className="inline-flex items-center gap-1.5 tabular-nums font-semibold text-[#1F3C88] hover:text-[#F7941D] hover:underline underline-offset-2 transition-colors duration-200 focus:outline-none"
        >
          {currency(net)}
          <Receipt className="w-3.5 h-3.5 opacity-60" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 bg-white border border-[#E5E5E2] shadow-xl" data-testid={`commission-audit-panel-${contract.id}`}>
        <div className="text-xs tracking-[0.14em] uppercase font-semibold text-[#F7941D] mb-1">Commission Audit</div>
        <div className="text-sm font-medium text-[#0A0A0A]">{contract.address || "This contract"}</div>
        <div className="text-xs text-[#a3a099] mb-3">{currency(contract.contract_value)} @ {contract.commission_rate}% · {contract.lead_generated_by || "—"}</div>
        <div className={`${row} border-t border-[#E5E5E2] pt-2`}>
          <span className="text-[#575755]">Gross Commission</span>
          <span className="tabular-nums font-semibold text-[#0A0A0A]">{currency(gross)}</span>
        </div>
        <div className="mt-1 border-t border-dashed border-[#E5E5E2] pt-1">
          {deductions.map((d) => (
            <div key={d.label} className={row}>
              <span className="text-[#575755]">− {d.label}</span>
              <span className="tabular-nums text-[#992B2B]">{d.value ? `(${currency(d.value)})` : currency(0)}</span>
            </div>
          ))}
        </div>
        <div className={`${row} border-t border-[#E5E5E2] mt-1 pt-2`}>
          <span className="font-semibold text-[#1F3C88]">Company Net</span>
          <span className="tabular-nums font-serif text-lg text-[#F7941D]">{currency(net)}</span>
        </div>
      </PopoverContent>
    </Popover>
  );
}
